import React, { FunctionComponent, createContext, useContext, useCallback, useMemo } from 'react';
import { DrawAction, DrawActionType, DrawActionCodec } from '@engoo/realtime-protocol/draw';
import { IColor } from '@engoo/realtime-protocol/util/color';
import { TwoParamCommand } from '@engoo/realtime-protocol/draw/trace';
import { Stream } from '@engoo/realtime-protocol/stream';
import { DummyLazyDocumentManager, LazyDocumentManager } from 'app/services/rtc/sync/documentmanagers/lazy';
import useDocumentManager from 'app/containers/rtc/DocumentProvider/useDocumentManager';
import { makeDrawDocumentSpec } from 'app/models/rtc/draw/documentSpec';
import DrawActionHandler from 'app/models/rtc/draw/actionHandler';
import { drawReducer, Surfaces, Surface } from 'app/models/rtc/draw/reducer';
import { Resolution } from 'app/models/rtc/draw/resolution';
import { KeyPath } from 'app/models/entityStateSync/keypath';
import { Map } from 'immutable';
import * as iots from 'io-ts';
import { useRtcState } from 'app/containers/rtc/RtcStateContext';
import { SIMPLIFICATION_TOLERANCE } from 'app/models/rtc/draw/constants';
import { makeCachedLoader } from 'app/containers/state/makeCachedLoader';
import { IMetadata } from 'app/services/rtc/sync/ActionStream';

interface IDrawContext {
  readonly surfaces: Surfaces;
  readonly addAction: (action: DrawAction) => void;
  readonly isBlocked: boolean;
  readonly metadata: IMetadata | undefined;
}

const dummyDrawContext: IDrawContext = {
  addAction: () => {},
  surfaces: Map(),
  isBlocked: true,
  metadata: undefined,
};

const DrawContext = createContext<IDrawContext>(dummyDrawContext);

const { useLoader: useSimplifyJSLoader } = makeCachedLoader(async (active: boolean) => {
  if (active) {
    return (await import('simplify-js')).default;
  } else {
    return undefined;
  }
});

/**
 * Configures document manager for draw document
 *
 * Provides two things for `DrawActionHandler`:
 * - local user
 * - lazily-loaded SimplifyJS implementation
 *
 * Loads SimplifyJS implementation only if local user is set.
 *
 * If some dependencies are missing, returns a spec for dummy
 * document manager to avoid having to render provider conditionally
 * because that would introduce reconciliation.
 */
const useDrawDocumentSpec = () => {
  const { caller } = useRtcState();

  // Only load SimplifyJS if we also have the caller
  const { value: simplifyJSImpl } = useSimplifyJSLoader({ args: Boolean(caller) });

  const simplifyDeps = useMemo(() => {
    if (simplifyJSImpl) {
      return { simplifyJSImpl, tolerance: SIMPLIFICATION_TOLERANCE };
    } else {
      return undefined;
    }
  }, [simplifyJSImpl]);

  const spec = useMemo(() => {
    if (caller?.id && simplifyDeps) {
      return makeDrawDocumentSpec({
        actionHandler: new DrawActionHandler(caller?.id, simplifyDeps),
        managerType: LazyDocumentManager,
      });
    } else {
      return makeDrawDocumentSpec({
        actionHandler: { apply: (state) => state, compress: () => [] },
        managerType: DummyLazyDocumentManager,
      });
    }
  }, [caller?.id, simplifyDeps]);

  return spec;
};

export const DrawProvider: FunctionComponent = ({ children }) => {
  const spec = useDrawDocumentSpec();
  const {
    documentState: { surfaces } = drawReducer(),
    manager,
    isBlocked,
    metadata,
  } = useDocumentManager(Stream.Draw, spec);

  const context = useMemo((): IDrawContext => ({
    isBlocked,
    surfaces,
    metadata,
    addAction: (action: DrawAction) => {
      if (!isBlocked) {
        manager?.addLocalAction(DrawActionCodec.encode(action));
      }
    },
  }), [manager, surfaces, isBlocked, metadata]);

  return <DrawContext.Provider value={context}>
    {children}
  </DrawContext.Provider>;
};

export const useDraw = () => useContext(DrawContext);

export const useSurfaceState = (keyPath: KeyPath) => {
  const { surfaces } = useDraw();
  const empty = useMemo(() => new Surface(), []);
  return surfaces.get(keyPath, empty);
};

export interface IUseDrawToolTemplate {
  /**
   * Start a new shape
   */
  startShape: (x: iots.Int, y: iots.Int) => void;
  /**
   * Add point to the most-recent shape created by the local user
   */
  addPoint: (x: iots.Int, y: iots.Int) => void
}

export interface IPencilTemplate {
  type: DrawActionType.Pencil;
  size: number;
  color: IColor;
}

export interface IEraserTemplate {
  type: DrawActionType.Eraser;
  size: number;
}

export type DrawToolTemplate = IPencilTemplate | IEraserTemplate;

/**
 * Exposes `startShape` and `addPoint` interface to draw using the provided start action template
 *
 * Start action's `template` should determine all initial properties
 * of the shape like stroke size and colour.
 */
export const useDrawToolTemplate = ({
  keyPath,
  resolution,
  template,
}: {
  /**
   * Key path to the surface being drawn on
   */
  keyPath: KeyPath,
  /**
   * Bounds of the coordinate system points provided to `startShape`
   * and `addPoint` methods
   */
  resolution: Resolution,
  /**
   * Template for the action produced by `startShape`
   *
   * Allowed values are all action types that have `trace` field
   * apart from `add` action. `trace` field can be omitted in the
   * template as it's overwritten anyway.
   */
  template: DrawToolTemplate,
}): IUseDrawToolTemplate => {
  const { addAction } = useDraw();

  const startShape = useCallback((x: iots.Int, y: iots.Int) => {
    // Send `switch` action lazily before action that starts new
    // trace. If we sent eagerly when `keyPath` or `resolution`
    // changes, we'd be potentially sending a lot of unnecessary
    // actions: Every time an image modal is open, each user would
    // be sending a `switch` action.
    //
    // It's OK to send redundant `switch` actions becase
    // `DrawActionHandler.compress` will filter out all unnecessary
    // actions before sending them to remote.
    addAction({
      type: DrawActionType.Switch,
      path: keyPath.toArray(),
      res: [resolution.width, resolution.height],
    });

    addAction({
      ...template,
      trace: [[TwoParamCommand.MoveAbsolute, x, y]],
    });
  }, [keyPath, resolution, addAction, template]);

  const addPoint = useCallback((x: iots.Int, y: iots.Int) => {
    // It's OK to spam the document manager with single action for
    // each point because it should concatenate adjacent actions
    // in the `compress` step of `DrawActionHandler`
    addAction({
      type: DrawActionType.Add,
      trace: [[TwoParamCommand.LineAbsolute, x, y]],
    });
  }, [addAction]);

  return useMemo(() => ({ startShape, addPoint }), [startShape, addPoint]);
};
